/**
 * AcceptPlanConfirmDialog
 *
 * Confirmation step shown before a suggested plan is accepted. Summarises how
 * many bookings will be created and for which resources, since creating the
 * bookings cannot be undone.
 * Evo Design System — semantic tokens only.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertTriangle, Check, Info, Loader2, Pencil } from 'lucide-react';
import { ProposedBookingSlot } from '@/types/resourceRequest';
import { useScheduler } from '@/contexts/SchedulerContext';
import { useDataMode } from '@/contexts/DataModeContext';
import { getCurrentResourceBookingTypeUsages, type BookingTypeUsageDto } from '@/services/api';

export interface AcceptPlanResourceSummary {
  resourceId:   string;
  resourceName: string;
  photoUrl?:    string;
  bookingCount: number;
  hours:        number;
  startDate:    string;
  endDate:      string;
}

function fmtDate(iso: string) {
  try { return format(parseISO(iso), 'd MMM yyyy'); } catch { return iso; }
}

function initials(name: string) {
  return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
}

/** Groups the (already edit-merged) slots into a per-resource summary. */
export function summariseSlotsByResource(slots: ProposedBookingSlot[]): AcceptPlanResourceSummary[] {
  const map = new Map<string, AcceptPlanResourceSummary>();
  for (const slot of slots) {
    const key      = String(slot.resourceId);
    const bookings = slot.dailyBreakdown?.length || 1;
    const existing = map.get(key);
    if (!existing) {
      map.set(key, {
        resourceId:   key,
        resourceName: slot.resourceName,
        photoUrl:     slot.resourcePhotoUrl,
        bookingCount: bookings,
        hours:        slot.hours,
        startDate:    slot.startDate,
        endDate:      slot.endDate,
      });
      continue;
    }
    existing.bookingCount += bookings;
    existing.hours        += slot.hours;
    if (slot.startDate < existing.startDate) existing.startDate = slot.startDate;
    if (slot.endDate   > existing.endDate)   existing.endDate   = slot.endDate;
  }
  return Array.from(map.values());
}

export interface AcceptPlanConfirmDialogProps {
  open:            boolean;
  onOpenChange:    (open: boolean) => void;
  /** Slots of the selected scenario with any in-drawer edits already applied */
  slots:           ProposedBookingSlot[];
  scenarioLabel:   string;
  projectName:     string;
  role:            string;
  /** Number of slots the user manually adjusted before accepting */
  editCount:       number;
  isAccepting:     boolean;
  /** Receives the Booking Type chosen for the bookings about to be created */
  onConfirm:       (bookingTypeId?: number) => void;
}

export function AcceptPlanConfirmDialog({
  open,
  onOpenChange,
  slots,
  scenarioLabel,
  projectName,
  role,
  editCount,
  isAccepting,
  onConfirm,
}: AcceptPlanConfirmDialogProps) {
  const { settings } = useScheduler();
  const { isDemo }   = useDataMode();
  const isDemoMode   = isDemo('calendar');
  const summaries = useMemo(() => summariseSlotsByResource(slots), [slots]);

  const bookingCount = summaries.reduce((sum, s) => sum + s.bookingCount, 0);
  const totalHours   = summaries.reduce((sum, s) => sum + s.hours, 0);

  // Every active Booking Type is listed regardless of permission, but only types
  // the user has full access to can actually be used to create the bookings.
  const activeBookingTypes = useMemo(
    () => (settings.bookingTypes ?? []).filter(bt => bt.active),
    [settings.bookingTypes],
  );

  const [usages, setUsages] = useState<BookingTypeUsageDto[] | null>(null);

  useEffect(() => {
    if (!open || isDemoMode) return;
    let cancelled = false;
    getCurrentResourceBookingTypeUsages()
      .then(result => { if (!cancelled) setUsages(result); })
      .catch(() => { if (!cancelled) setUsages([]); });
    return () => { cancelled = true; };
  }, [open, isDemoMode]);

  const hasFullAccess = useCallback((bookingTypeId: string) => {
    if (usages == null) return true; // permissions still loading
    return usages.some(u => String(u.bookingTypeId) === bookingTypeId && u.isFullAccess);
  }, [usages]);

  // Preselect the configured default, but skip straight to a permitted type when
  // the default is one the user cannot use, so the dialog opens ready to confirm.
  const defaultBookingTypeId = useMemo(() => {
    const configured = activeBookingTypes.find(
      bt => String(bt.id) === String(settings.timelineDefaultBookingType),
    );
    const fallback = (configured && hasFullAccess(String(configured.id)) ? configured : undefined)
      ?? activeBookingTypes.find(bt => hasFullAccess(String(bt.id)))
      ?? configured
      ?? activeBookingTypes[0];
    return fallback?.id != null ? String(fallback.id) : '';
  }, [activeBookingTypes, settings.timelineDefaultBookingType, hasFullAccess]);

  const [selectedBookingTypeId, setSelectedBookingTypeId] = useState('');

  useEffect(() => {
    if (!open) return;
    setSelectedBookingTypeId(prev => prev || defaultBookingTypeId);
  }, [open, defaultBookingTypeId]);

  const lacksFullAccess = selectedBookingTypeId !== '' && !hasFullAccess(selectedBookingTypeId);

  return (
    <AlertDialog open={open} onOpenChange={isAccepting ? undefined : onOpenChange}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-warning" />
            Confirm plan
          </AlertDialogTitle>
          <AlertDialogDescription>
            You are about to create{' '}
            <span className="font-semibold text-foreground">
              {bookingCount} booking{bookingCount !== 1 ? 's' : ''}
            </span>
            {' for '}
            <span className="font-semibold text-foreground">
              {summaries.length} resource{summaries.length !== 1 ? 's' : ''}
            </span>
            {' on '}
            <span className="font-semibold text-foreground">{projectName}</span>
            {role ? <> as <span className="font-semibold text-foreground">{role}</span></> : null}
            {'. Do you want to continue?'}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3">
          <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
            <Badge variant="secondary" className="text-[10px] h-5">{scenarioLabel}</Badge>
            <span>{totalHours}h total</span>
            {editCount > 0 && (
              <Badge variant="secondary" className="text-[10px] h-5 gap-1">
                <Pencil className="h-2.5 w-2.5" />
                {editCount} booking{editCount !== 1 ? 's' : ''} adjusted
              </Badge>
            )}
          </div>

          {activeBookingTypes.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="accept-plan-booking-type" className="text-xs">Booking type</Label>
              <Select
                value={selectedBookingTypeId}
                onValueChange={setSelectedBookingTypeId}
                disabled={isAccepting}
              >
                <SelectTrigger id="accept-plan-booking-type" className="h-8 text-xs">
                  <SelectValue placeholder="Select a booking type" />
                </SelectTrigger>
                <SelectContent>
                  {activeBookingTypes.map(bt => (
                    <SelectItem key={String(bt.id)} value={String(bt.id)} className="text-xs">
                      {bt.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {lacksFullAccess && (
                <p className="flex items-start gap-1.5 text-[11px] text-warning">
                  <Info className="h-3 w-3 mt-0.5 shrink-0" />
                  You do not have permission to use this booking type. Select a different
                  booking type to create these bookings.
                </p>
              )}
            </div>
          )}

          <ul className="max-h-56 overflow-y-auto rounded-lg border divide-y">
            {summaries.map(s => (
              <li key={s.resourceId} className="flex items-center gap-3 px-3 py-2">
                <Avatar className="h-7 w-7 shrink-0">
                  {s.photoUrl && <AvatarImage src={s.photoUrl} alt={s.resourceName} />}
                  <AvatarFallback className="text-[10px]">{initials(s.resourceName)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium truncate">{s.resourceName}</p>
                  <p className="text-[11px] text-muted-foreground truncate">
                    {fmtDate(s.startDate)} – {fmtDate(s.endDate)}
                  </p>
                </div>
                <span className="text-[11px] text-muted-foreground shrink-0">
                  {s.bookingCount} booking{s.bookingCount !== 1 ? 's' : ''} · {s.hours}h
                </span>
              </li>
            ))}
          </ul>

          <Alert variant="warning" size="compact">
            <AlertDescription>
              Once created, these bookings cannot be undone automatically — they must be
              removed individually from the planner.
            </AlertDescription>
          </Alert>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isAccepting}>Back</AlertDialogCancel>
          <AlertDialogAction
            onClick={e => {
              e.preventDefault();
              const parsed = Number(selectedBookingTypeId);
              onConfirm(Number.isFinite(parsed) && parsed > 0 ? parsed : undefined);
            }}
            disabled={isAccepting || !selectedBookingTypeId || lacksFullAccess}
            className="gap-1.5"
          >
            {isAccepting ? (
              <><Loader2 className="h-3.5 w-3.5 animate-spin" />Creating bookings…</>
            ) : (
              <><Check className="h-3.5 w-3.5" />Create bookings</>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
